export const HISTOGRAM_MIN_MS = 0.5
export const HISTOGRAM_MAX_MS = 2000
export const HISTOGRAM_BUCKETS = 96

export interface DurationHistogram {
  readonly count: number
  add(valueMs: number, bucket?: number): void
  percentile(p: number): number | null
  reset(): void
}

const LOG_RANGE = Math.log(HISTOGRAM_MAX_MS / HISTOGRAM_MIN_MS)

export function histogramBucket(valueMs: number): number {
  if (valueMs <= HISTOGRAM_MIN_MS) return 0
  const index = Math.floor((Math.log(valueMs / HISTOGRAM_MIN_MS) / LOG_RANGE) * HISTOGRAM_BUCKETS)
  return Math.min(HISTOGRAM_BUCKETS - 1, index)
}

function bucketMidpoint(index: number): number {
  const lower = HISTOGRAM_MIN_MS * Math.exp((index / HISTOGRAM_BUCKETS) * LOG_RANGE)
  const upper = HISTOGRAM_MIN_MS * Math.exp(((index + 1) / HISTOGRAM_BUCKETS) * LOG_RANGE)
  return Math.sqrt(lower * upper)
}

export function createDurationHistogram(): DurationHistogram {
  const buckets = new Uint32Array(HISTOGRAM_BUCKETS)
  let count = 0
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  return {
    get count() {
      return count
    },
    add(valueMs, bucket) {
      if (!Number.isFinite(valueMs) || valueMs < 0) return
      const index = bucket ?? histogramBucket(valueMs)
      buckets[index] = (buckets[index] ?? 0) + 1
      count += 1
      if (valueMs < min) min = valueMs
      if (valueMs > max) max = valueMs
    },
    percentile(p) {
      if (count === 0) return null
      const clamped = Math.min(100, Math.max(0, p))
      const rank = Math.max(1, Math.ceil((clamped / 100) * count))
      let seen = 0
      for (let index = 0; index < HISTOGRAM_BUCKETS; index++) {
        seen += buckets[index] ?? 0
        if (seen >= rank) return Math.min(max, Math.max(min, bucketMidpoint(index)))
      }
      return max
    },
    reset() {
      buckets.fill(0)
      count = 0
      min = Number.POSITIVE_INFINITY
      max = Number.NEGATIVE_INFINITY
    },
  }
}
